import { useState } from "react";
import {
  addRecord,
  addSource,
  BLANK,
  removeRecord,
  reviseRecord,
  UNKNOWN,
  type Account,
  type Amount,
  type EconomicEvent,
  type ExpenseCategory,
  type ExpenseTreatment,
  type Firm,
  type FormStatus,
  type PayoutDates,
  type Source,
  type YearFile,
} from "@ftr/tax-core";
import { Card } from "../components/ui.tsx";
import { newId, nowIso } from "../lib/yearStore.ts";
import { useOrganizer } from "./context.tsx";
import { Editor, IssuesFor, personName, Row, StateBadge } from "./common.tsx";
import { AmountInput, AmountText, amountOk, Chips, DateInput, TextInput, YesNoField } from "./fields.tsx";
import { FormStatusChips, fmtUsd } from "./FuturesStep.tsx";
import { shortDate } from "../lib/format.ts";
import { candidateFingerprint, type FormCandidate, type PayerFormCandidate } from "../import/parse1099.ts";
import { ImportedBanner, importHint, ImportPanel, type ImportedFile } from "./ImportPanel.tsx";

type Payout = Extract<EconomicEvent, { kind: "compensation" }>;
type Expense = Extract<EconomicEvent, { kind: "expense" }>;
type Refund = Extract<EconomicEvent, { kind: "refund" }>;
type PayerForm = Extract<Source, { kind: "form_1099nec" | "form_1099misc" | "form_1099k" }>;

/** Firm brands with legal payer names confirmed from their own terms (Oct 2026). Others are left for the user to fill in. */
export const FIRMS: { name: string; payerNames: string[] }[] = [
  { name: "Topstep", payerNames: [] },
  { name: "Apex Trader Funding", payerNames: ["Apex Trader Funding Inc."] },
  { name: "Tradeify", payerNames: ["Tradeify Holdings, Corp.", "Novaflame LTD"] },
  { name: "Take Profit Trader", payerNames: [] },
  { name: "MyFundedFutures", payerNames: [] },
  { name: "Earn2Trade", payerNames: [] },
  { name: "Lucid Trading", payerNames: [] },
  { name: "Alpha Futures", payerNames: [] },
  { name: "FundedNext Futures", payerNames: [] },
  { name: "Bulenox", payerNames: [] },
  { name: "Elite Trader Funding", payerNames: [] },
  { name: "TradeDay", payerNames: [] },
  { name: "Funded Futures Family", payerNames: [] },
  { name: "Top One Futures", payerNames: [] },
  { name: "Phidias", payerNames: [] },
];

const CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: "evaluation", label: "Evaluation" },
  { value: "reset", label: "Reset" },
  { value: "activation", label: "Activation" },
  { value: "data", label: "Market data" },
  { value: "platform", label: "Platform" },
  { value: "payout_fee", label: "Payout fee" },
  { value: "education", label: "Education" },
  { value: "other", label: "Other" },
];

const TREATMENTS: { value: ExpenseTreatment; label: string }[] = [
  { value: "current", label: "Business expense" },
  { value: "startup", label: "Startup cost" },
  { value: "personal", label: "Personal" },
  { value: "unresolved", label: "Not sure" },
];

const FORM_KINDS: { value: PayerForm["kind"]; label: string; box: string }[] = [
  { value: "form_1099nec", label: "1099-NEC", box: "Box 1a, nonemployee compensation" },
  { value: "form_1099misc", label: "1099-MISC", box: "Box 3, other income" },
  { value: "form_1099k", label: "1099-K", box: "Box 1a, gross amount of payment transactions" },
];

const live = <T extends { deletedAt: string | null }>(l: T[]) => l.filter((x) => x.deletedAt === null);

export function PropStep() {
  const { yf, update, analysis } = useOrganizer();
  const firms = live(yf.firms);
  const [adding, setAdding] = useState(false);
  return (
    <div className="steps-body">
      <Card title="Prop firms that paid you">
        <p className="card-intro">
          Add each prop firm you had an account with in 2026, even ones that didn't pay out: their fees can still matter. Enter payouts from the firm's payout
          history; the 1099, processor receipts and bank deposits are checked against them, never added on top.
        </p>
        <div className="module-line">
          {yf.people.map((p) => {
            const r = analysis.modules.propBusiness[p.id];
            return r ? (
              <span key={p.id} className="module-chip">
                <StateBadge state={r.state} />
                <span>Schedule C{yf.people.length > 1 ? ` · ${personName(yf, p.id)}` : ""}</span>
              </span>
            ) : null;
          })}
        </div>
        {firms.map((f) => (
          <FirmCard key={f.id} firm={f} />
        ))}
        {adding ? (
          <FirmEditor onDone={() => setAdding(false)} />
        ) : (
          <button type="button" className="btn btn--outline" onClick={() => setAdding(true)}>
            + Add a prop firm
          </button>
        )}
        <div className="form-section">
          <YesNoField
            legend="Is that every prop firm you had an account with in 2026?"
            hint="If you don't trade with prop firms, answer yes with none listed."
            value={yf.household.allPropFirmsListed}
            onChange={(v) => update((y) => ({ ...y, household: { ...y.household, allPropFirmsListed: v } }))}
          />
        </div>
      </Card>
    </div>
  );
}

function FirmEditor({ onDone }: { onDone: () => void }) {
  const { yf, update } = useOrganizer();
  const primary = yf.people.find((p) => p.role === "primary")!;
  const [name, setName] = useState("");
  const [ownerId, setOwner] = useState(primary.id);
  const [status, setStatus] = useState<FormStatus>(null);
  const save = () => {
    const known = FIRMS.find((f) => f.name.toLowerCase() === name.trim().toLowerCase());
    const existing = live(yf.firms).find((f) => f.name.toLowerCase() === name.trim().toLowerCase());
    const firmId = existing?.id ?? newId();
    update((y) => {
      let next = existing ? y : addRecord(y, "firms", { id: firmId, name: known?.name ?? name.trim(), payerNames: known?.payerNames ?? [] });
      next = addRecord(next, "accounts", { id: newId(), kind: "prop_firm", ownerId, firmId, label: known?.name ?? name.trim(), coverageComplete: null, forms: { form_1099nec: status } });
      return next;
    });
    onDone();
  };
  return (
    <Editor title="New prop firm" onSave={save} onCancel={onDone} canSave={name.trim().length > 0}>
      <TextInput label="Firm" value={name} onChange={setName} list="ftr-firms" placeholder="Start typing, or enter any firm" />
      <datalist id="ftr-firms">
        {FIRMS.map((f) => (
          <option key={f.name} value={f.name} />
        ))}
      </datalist>
      {yf.people.length > 1 && <Chips legend="Who traded with this firm?" value={ownerId} options={yf.people.map((p) => ({ value: p.id, label: personName(yf, p.id) }))} onChange={setOwner} />}
      <FormStatusChips value={status} onChange={setStatus} form="1099" />
      <p className="field-hint">Firms usually send a 1099-NEC when they pay you $2,000 or more in 2026. Some firms' help pages still say $600; that was the old rule.</p>
    </Editor>
  );
}

function FirmCard({ firm }: { firm: Firm }) {
  const { yf, update, analysis } = useOrganizer();
  const accounts = live(yf.accounts).filter((a) => a.kind === "prop_firm" && a.firmId === firm.id);
  const ids = new Set(accounts.map((a) => a.id));
  const events = live(yf.events);
  const payouts = events.filter((e): e is Payout => e.kind === "compensation" && e.accountId !== null && ids.has(e.accountId));
  const expenses = events.filter((e): e is Expense => e.kind === "expense" && e.accountId !== null && ids.has(e.accountId));
  const refunds = events.filter((e): e is Refund => e.kind === "refund" && e.accountId !== null && ids.has(e.accountId));
  const forms = live(yf.sources).filter((s): s is PayerForm => (s.kind === "form_1099nec" || s.kind === "form_1099misc" || s.kind === "form_1099k") && s.firmId === firm.id);
  const recon = analysis.reconciliation.prop.filter((r) => r.firmId === firm.id);
  const excluded = new Map(analysis.excludedSources.map((e) => [e.sourceId, e.reason]));
  const [editing, setEditing] = useState<{ kind: "payout" | "form" | "expense" | "refund"; id: string | null } | null>(null);
  const account = accounts[0];

  const removeFirm = () => {
    if (!confirm(`Remove ${firm.name} and everything entered for it? You can't undo this.`)) return;
    update((y) => {
      let next = removeRecord(y, "firms", firm.id, nowIso());
      for (const a of accounts) next = removeRecord(next, "accounts", a.id, nowIso());
      for (const e of y.events) if (e.deletedAt === null && e.accountId && ids.has(e.accountId)) next = removeRecord(next, "events", e.id, nowIso());
      for (const s of y.sources) if (s.deletedAt === null && (s.firmId === firm.id || (s.accountId && ids.has(s.accountId)))) next = removeRecord(next, "sources", s.id, nowIso());
      return next;
    });
  };

  if (!account) return null;
  const counted = new Set(
    Object.values(analysis.modules.propBusiness).flatMap((r) => (r.state === "not_applicable" ? [] : ((r.state === "blocked" ? r.partial : r.value)?.perFirm.find((p) => p.firmId === firm.id)?.payoutIds ?? []))),
  );

  return (
    <div className="acct">
      <div className="acct-head">
        <div>
          <p className="acct-name">{firm.name}</p>
          <p className="acct-meta">
            {yf.people.length > 1 && <>{personName(yf, account.ownerId)} · </>}
            {payouts.length} payout{payouts.length === 1 ? "" : "s"} · {expenses.length} expense{expenses.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="acct-figure">
          {recon[0] ? (
            <>
              <span className="acct-figure-label">Payouts counted</span>
              <span className="figure">{fmtUsd(recon[0].eventGross)}</span>
            </>
          ) : null}
        </div>
      </div>

      <p className="sub-head">Payouts</p>
      {payouts.length === 0 && <p className="muted small">None yet.</p>}
      {payouts
        .slice()
        .sort((a, b) => ((a.dates.paid ?? "") < (b.dates.paid ?? "") ? -1 : 1))
        .map((p) => (
          <Row key={p.id} actions={<RowActions onEdit={() => setEditing({ kind: "payout", id: p.id })} onRemove={() => update((y) => removeRecord(y, "events", p.id, nowIso()))} />}>
            <p className="row-title">
              <AmountText a={p.gross} /> {p.subtype !== "payout" && <span className="pill pill--muted">{p.subtype}</span>}
              {!counted.has(p.id) && <span className="pill pill--warn">Not counted</span>}
            </p>
            <p className="row-sub">
              {p.dates.paid ? `Available ${shortDate(p.dates.paid)}` : "Date available not entered"} · fee <AmountText a={p.payoutFee} />
            </p>
          </Row>
        ))}

      <p className="sub-head">Tax forms from this firm or its payment processor</p>
      {forms.length === 0 && <p className="muted small">None yet.</p>}
      {forms.map((f) => (
        <Row key={f.id} actions={<RowActions onEdit={() => setEditing({ kind: "form", id: f.id })} onRemove={() => update((y) => removeRecord(y, "sources", f.id, nowIso()))} />}>
          <p className="row-title">
            {FORM_KINDS.find((k) => k.value === f.kind)!.label}
            {f.correctedAsPrinted ? " (corrected)" : ""} · <AmountText a={f.incomeBox} />
            {f.provenance === "imported" && <span className="pill pill--muted">from PDF</span>}
            {excluded.get(f.id) && <span className="pill pill--warn">Not used</span>}
          </p>
          <p className="row-sub">{f.issuer || "Payer not entered"}</p>
        </Row>
      ))}
      {recon.map((r) =>
        r.variance && r.variance !== "0" ? (
          <p key={r.personId} className="recon-line warn-text">
            1099 total differs from the payouts counted by {fmtUsd(r.variance)}.
          </p>
        ) : r.variance === "0" ? (
          <p key={r.personId} className="recon-line good-text">
            1099 matches the payouts counted.
          </p>
        ) : null,
      )}

      <p className="sub-head">Fees and costs</p>
      {expenses.length === 0 && <p className="muted small">None yet.</p>}
      {expenses.map((x) => (
        <Row key={x.id} actions={<RowActions onEdit={() => setEditing({ kind: "expense", id: x.id })} onRemove={() => update((y) => removeRecord(y, "events", x.id, nowIso()))} />}>
          <p className="row-title">
            {CATEGORIES.find((c) => c.value === x.category)?.label} · <AmountText a={x.amount} />
            <span className={`pill ${x.treatment === "current" ? "pill--good" : "pill--muted"}`}>{TREATMENTS.find((t) => t.value === (x.treatment ?? "unresolved"))?.label}</span>
          </p>
          <p className="row-sub">
            {x.vendor} · {x.date ? shortDate(x.date) : "no date"}
          </p>
        </Row>
      ))}
      {refunds.map((r) => (
        <Row key={r.id} actions={<RowActions onEdit={() => setEditing({ kind: "refund", id: r.id })} onRemove={() => update((y) => removeRecord(y, "events", r.id, nowIso()))} />}>
          <p className="row-title">
            Refund · <AmountText a={r.amount} />
          </p>
          <p className="row-sub">{r.date ? shortDate(r.date) : "no date"}</p>
        </Row>
      ))}

      <IssuesFor ids={[firm.id, ...accounts.map((a) => a.id), ...payouts.map((p) => p.id), ...forms.map((f) => f.id), ...expenses.map((x) => x.id), ...refunds.map((r) => r.id)]} exclude={["SCREENING_NEEDED", "COVERAGE_INCOMPLETE"]} />

      {editing?.kind === "payout" && <PayoutEditor account={account} existing={payouts.find((p) => p.id === editing.id) ?? null} onDone={() => setEditing(null)} />}
      {editing?.kind === "form" && <PayerFormEditor firm={firm} account={account} existing={forms.find((f) => f.id === editing.id) ?? null} onDone={() => setEditing(null)} />}
      {editing?.kind === "expense" && <ExpenseEditor firm={firm} account={account} existing={expenses.find((x) => x.id === editing.id) ?? null} payouts={payouts} onDone={() => setEditing(null)} />}
      {editing?.kind === "refund" && <RefundEditor account={account} existing={refunds.find((x) => x.id === editing.id) ?? null} expenses={expenses} onDone={() => setEditing(null)} />}

      {!editing && (
        <div className="acct-actions">
          <button type="button" className="btn btn--outline btn--sm" onClick={() => setEditing({ kind: "payout", id: null })}>+ Payout</button>
          <button type="button" className="btn btn--outline btn--sm" onClick={() => setEditing({ kind: "expense", id: null })}>+ Fee or cost</button>
          <button type="button" className="btn btn--outline btn--sm" onClick={() => setEditing({ kind: "form", id: null })}>+ 1099</button>
          <button type="button" className="btn btn--outline btn--sm" onClick={() => setEditing({ kind: "refund", id: null })}>+ Refund</button>
          <button type="button" className="link-btn link-btn--danger" onClick={removeFirm}>Remove firm</button>
        </div>
      )}

      <div className="form-section">
        <FormStatusChips value={account.forms.form_1099nec ?? null} onChange={(v) => update((y) => reviseRecord(y, "accounts", account.id, { forms: { ...account.forms, form_1099nec: v } }))} form="1099" />
        <YesNoField
          legend={`Have you entered every 2026 payout and fee from ${firm.name}?`}
          hint="Check the firm's payout history and your card statements for evaluation, reset and data fees."
          value={account.coverageComplete}
          onChange={(v) => update((y) => reviseRecord(y, "accounts", account.id, { coverageComplete: v }))}
        />
      </div>
    </div>
  );
}

function RowActions({ onEdit, onRemove }: { onEdit: () => void; onRemove: () => void }) {
  return (
    <>
      <button type="button" className="link-btn" onClick={onEdit}>Edit</button>
      <button type="button" className="link-btn" onClick={() => confirm("Remove this?") && onRemove()}>Remove</button>
    </>
  );
}

const NO_DATES: PayoutDates = { requested: null, approved: null, sentByPayer: null, paid: null, deposited: null };

function PayoutEditor({ account, existing, onDone }: { account: Account; existing: Payout | null; onDone: () => void }) {
  const { update } = useOrganizer();
  const [gross, setGross] = useState<Amount>(existing?.gross ?? UNKNOWN);
  const [fee, setFee] = useState<Amount>(existing?.payoutFee ?? UNKNOWN);
  const [dates, setDates] = useState<PayoutDates>(existing?.dates ?? NO_DATES);
  const [subtype, setSubtype] = useState<Payout["subtype"]>(existing?.subtype ?? "payout");
  const setDate = (k: keyof PayoutDates) => (v: string | null) => setDates((d) => ({ ...d, [k]: v }));
  const ownerId = account.ownerId === "joint" ? "" : account.ownerId;

  const save = () => {
    const fields = { gross, payoutFee: fee, dates, subtype };
    update((y) =>
      existing
        ? reviseRecord(y, "events", existing.id, fields as Partial<EconomicEvent>)
        : addRecord(y, "events", { id: newId(), kind: "compensation", personId: ownerId, accountId: account.id, createdFrom: { manual: true }, note: null, ...fields } as never),
    );
    onDone();
  };
  return (
    <Editor title={existing ? "Edit payout" : "New payout"} onSave={save} onCancel={onDone} canSave={gross.state === "value" && amountOk(gross) && amountOk(fee)}>
      <Chips
        legend="What was it?"
        value={subtype}
        options={[
          { value: "payout", label: "Payout" },
          { value: "bonus", label: "Bonus" },
          { value: "referral", label: "Referral" },
          { value: "contest", label: "Contest prize" },
          { value: "other", label: "Other" },
        ]}
        onChange={setSubtype}
      />
      <AmountInput
        label="Your share, before any payout fee"
        hint="After the firm's profit split. If you requested $5,000 and the firm kept 10%, enter 4,500."
        value={gross}
        onChange={setGross}
        emptyMeans="unknown"
      />
      <AmountInput label="Payout fee" hint="A transfer or processing fee taken from this payout. It's deducted as an expense, once." value={fee} onChange={setFee} emptyMeans="unknown" none={{ label: "No fee" }} />
      <div className="box-grid">
        <DateInput label="Requested" value={dates.requested} onChange={setDate("requested")} />
        <DateInput label="Approved" value={dates.approved} onChange={setDate("approved")} />
        <DateInput
          label="Available to you"
          hint="The first day you could withdraw or spend it: in your wallet, processor account or bank, whichever came first."
          value={dates.paid}
          onChange={setDate("paid")}
        />
        <DateInput label="Reached your bank" value={dates.deposited} onChange={setDate("deposited")} hint="Leave empty if it stayed in a wallet." />
      </div>
    </Editor>
  );
}

function PayerFormEditor({ firm, account, existing, onDone }: { firm: Firm; account: Account; existing: PayerForm | null; onDone: () => void }) {
  const { yf, update, analysis } = useOrganizer();
  const [kind, setKind] = useState<PayerForm["kind"]>(existing?.kind ?? "form_1099nec");
  const [issuer, setIssuer] = useState(existing?.issuer ?? firm.payerNames[0] ?? "");
  const [income, setIncome] = useState<Amount>(existing?.incomeBox ?? UNKNOWN);
  const [box4, setBox4] = useState<Amount>(existing?.box4 ?? BLANK);
  const [corrected, setCorrected] = useState(existing?.correctedAsPrinted ?? false);
  const [relation, setRelation] = useState<"corrects" | "separate" | "same" | null>(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<{ cand: PayerFormCandidate; file: ImportedFile } | null>(null);
  // Bumped when an import fills the boxes, so the amount inputs re-read their values.
  const [fillKey, setFillKey] = useState(0);
  const useImport = (c: FormCandidate, file: ImportedFile) => {
    if (c.kind === "form_1099b") return;
    setKind(c.kind);
    setIssuer(c.issuer.found ? c.issuer.value : "");
    setIncome(c.incomeBox.found ? c.incomeBox.value : UNKNOWN);
    setBox4(c.box4.found ? c.box4.value : BLANK);
    setCorrected(c.corrected.found ? c.corrected.value : false);
    setImported({ cand: c, file });
    setImporting(false);
    setFillKey((k) => k + 1);
  };
  const clearImport = () => {
    setIssuer(firm.payerNames[0] ?? "");
    setIncome(UNKNOWN);
    setBox4(BLANK);
    setCorrected(false);
    setImported(null);
    setFillKey((k) => k + 1);
  };
  const superseded = new Set(analysis.excludedSources.filter((e) => e.reason === "superseded" || e.reason === "voided").map((e) => e.sourceId));
  const ownerId = account.ownerId === "joint" ? yf.people[0]!.id : account.ownerId;
  const others = existing
    ? []
    : live(yf.sources).filter(
        (s): s is PayerForm => s.kind === kind && s.firmId === firm.id && s.personId === ownerId && !superseded.has(s.id) && s.issuer.trim().toLowerCase() === issuer.trim().toLowerCase(),
      );
  const meta = FORM_KINDS.find((k) => k.value === kind)!;

  const save = () => {
    if (existing) {
      update((y) => reviseRecord(y, "sources", existing.id, { issuer, incomeBox: income, box4, correctedAsPrinted: corrected } as Partial<Source>));
      return onDone();
    }
    if (relation === "same") return onDone();
    const id = newId();
    update((y) => {
      let next = addSource(y, {
        id,
        kind,
        taxYear: imported?.cand.taxYear.found ? imported.cand.taxYear.value : y.taxYear,
        fingerprint: imported ? candidateFingerprint(imported.file.hash, imported.cand) : id,
        fileHash: imported?.file.hash ?? null,
        personId: ownerId,
        accountId: account.id,
        firmId: firm.id,
        provenance: imported ? "imported" : "entered",
        addedAt: nowIso(),
        issuer,
        correctedAsPrinted: corrected || relation === "corrects",
        incomeBox: income,
        box4,
      } as Parameters<typeof addSource>[1]).yearFile;
      next = relate(next, id, others, relation);
      if (issuer.trim() && !firm.payerNames.some((n) => n.toLowerCase() === issuer.trim().toLowerCase()))
        next = reviseRecord(next, "firms", firm.id, { payerNames: [...firm.payerNames, issuer.trim()] });
      return next;
    });
    onDone();
  };

  return (
    <Editor title={existing ? "Edit form" : `Tax form for ${firm.name}`} onSave={save} onCancel={onDone} canSave={amountOk(income) && amountOk(box4) && (!others.length || relation !== null)}>
      {!existing && !importing && !imported && (
        <button type="button" className="btn btn--outline btn--sm" onClick={() => setImporting(true)}>
          Import from PDF
        </button>
      )}
      {importing && <ImportPanel want="payer" onUse={useImport} onClose={() => setImporting(false)} />}
      {imported && <ImportedBanner file={imported.file} taxYear={imported.cand.taxYear} organizerYear={yf.taxYear} onClear={clearImport} />}
      {!existing && (
        <Chips
          legend="Which form?"
          value={kind}
          options={FORM_KINDS.map((k) => ({ value: k.value, label: k.label }))}
          onChange={setKind}
          hint={imported ? `The PDF reads as a ${FORM_KINDS.find((k) => k.value === imported.cand.kind)?.label}. Check the form's title.` : undefined}
        />
      )}
      <div key={fillKey}>
        <TextInput
          label="Payer's name (top left of the form)"
          value={issuer}
          onChange={setIssuer}
          hint={imported ? importHint(imported.cand.issuer, "copy it from the top left of the form") : "It may be the firm, a related company, or a payment processor such as Rise."}
        />
        <AmountInput label={meta.box} value={income} onChange={setIncome} emptyMeans="blank" hint={imported ? importHint(imported.cand.incomeBox) : undefined} />
        <AmountInput label="Box 4, federal income tax withheld" value={box4} onChange={setBox4} emptyMeans="blank" hint={imported ? importHint(imported.cand.box4) : undefined} />
        <label className="check">
          <input type="checkbox" checked={corrected} onChange={(e) => setCorrected(e.target.checked)} />
          The form has the CORRECTED box checked
        </label>
        {imported && <p className="field-hint">{importHint(imported.cand.corrected, "look for a check mark in the CORRECTED box at the top of the form")}</p>}
      </div>
      {others.length > 0 && (
        <div className="ask">
          <Chips
            legend={`There's already a ${meta.label} from ${issuer || "this payer"}. How does this one relate to it?`}
            value={relation}
            options={[
              { value: "corrects", label: "It corrects the earlier one" },
              { value: "separate", label: "It's a separate form" },
              { value: "same", label: "It's the same form again" },
            ]}
            onChange={setRelation}
          />
        </div>
      )}
    </Editor>
  );
}

/** Record how a newly added form relates to forms already there: a confirmed correction, or confirmed separate forms. */
function relate(y: YearFile, id: string, others: { id: string; revision: number }[], relation: "corrects" | "separate" | "same" | null): YearFile {
  if (!others.length || !relation) return y;
  if (relation === "corrects") {
    const orig = others[others.length - 1]!;
    return addRecord(y, "corrections", { id: newId(), originalId: orig.id, replacementId: id, status: "confirmed", confirmedRevisions: { original: orig.revision, replacement: 1 } });
  }
  if (relation === "separate") {
    const all = [...others.map((o) => ({ id: o.id, rev: o.revision })), { id, rev: 1 }];
    return addRecord(y, "decisions", { id: newId(), kind: "confirm_both_info_returns", sourceIds: all.map((a) => a.id), revisions: all.map((a) => a.rev) });
  }
  return y;
}

function ExpenseEditor({ firm, account, existing, payouts, onDone }: { firm: Firm; account: Account; existing: Expense | null; payouts: Payout[]; onDone: () => void }) {
  const { update } = useOrganizer();
  const [category, setCategory] = useState<ExpenseCategory>(existing?.category ?? "evaluation");
  const [vendor, setVendor] = useState(existing?.vendor ?? firm.name);
  const [amount, setAmount] = useState<Amount>(existing?.amount ?? UNKNOWN);
  const [date, setDate] = useState<string | null>(existing?.date ?? null);
  const [treatment, setTreatment] = useState<ExpenseTreatment | null>(existing?.treatment ?? null);
  const [relates, setRelates] = useState<string | null>(existing?.relatesToEventId ?? null);
  const ownerId = account.ownerId === "joint" ? "" : account.ownerId;
  const save = () => {
    const fields = { category, vendor, amount, date, treatment, relatesToEventId: category === "payout_fee" ? relates : null };
    update((y) =>
      existing
        ? reviseRecord(y, "events", existing.id, fields as Partial<EconomicEvent>)
        : addRecord(y, "events", { id: newId(), kind: "expense", personId: ownerId, accountId: account.id, createdFrom: { manual: true }, note: null, ...fields } as never),
    );
    onDone();
  };
  return (
    <Editor title={existing ? "Edit cost" : "New fee or cost"} onSave={save} onCancel={onDone} canSave={amount.state === "value" && amountOk(amount) && date !== null}>
      <Chips legend="What was it for?" value={category} options={CATEGORIES} onChange={setCategory} />
      {category === "payout_fee" && (
        <>
          <p className="field-hint">If the fee is already entered on the payout itself, don't add it again here.</p>
          {payouts.length > 0 && (
            <Chips
              legend="Which payout was it taken from?"
              value={relates}
              options={payouts.map((p) => ({ value: p.id, label: `${p.gross.state === "value" ? fmtUsd(p.gross.value) : "?"}${p.dates.paid ? ` · ${shortDate(p.dates.paid)}` : ""}` }))}
              onChange={setRelates}
            />
          )}
        </>
      )}
      <TextInput label="Paid to" value={vendor} onChange={setVendor} />
      <AmountInput label="Amount" value={amount} onChange={setAmount} emptyMeans="unknown" />
      <DateInput label="Date charged" value={date} onChange={setDate} />
      <Chips
        legend="How should it be treated?"
        value={treatment}
        options={TREATMENTS}
        onChange={setTreatment}
        hint="Fees for a trading business you'd already started are usually business expenses. Costs from before you started can be startup costs, which follow different rules. If you're not sure, choose Not sure: it's kept for your preparer and not deducted."
      />
    </Editor>
  );
}

function RefundEditor({ account, existing, expenses, onDone }: { account: Account; existing: Refund | null; expenses: Expense[]; onDone: () => void }) {
  const { update } = useOrganizer();
  const [amount, setAmount] = useState<Amount>(existing?.amount ?? UNKNOWN);
  const [date, setDate] = useState<string | null>(existing?.date ?? null);
  const initial = existing ? ("eventId" in existing.refunds ? existing.refunds.eventId : "prior") : null;
  const [target, setTarget] = useState<string | null>(initial);
  const [deducted, setDeducted] = useState<"yes" | "no" | "unsure" | null>(
    existing && "priorYear" in existing.refunds ? (existing.refunds.deductedThen === null ? "unsure" : existing.refunds.deductedThen ? "yes" : "no") : null,
  );
  const ownerId = account.ownerId === "joint" ? "" : account.ownerId;
  const save = () => {
    const refunds = target === "prior" ? { priorYear: true as const, deductedThen: deducted === "unsure" || deducted === null ? null : deducted === "yes" } : { eventId: target! };
    const fields = { amount, date, refunds };
    update((y) =>
      existing
        ? reviseRecord(y, "events", existing.id, fields as Partial<EconomicEvent>)
        : addRecord(y, "events", { id: newId(), kind: "refund", personId: ownerId, accountId: account.id, createdFrom: { manual: true }, note: null, ...fields } as never),
    );
    onDone();
  };
  return (
    <Editor title={existing ? "Edit refund" : "New refund"} onSave={save} onCancel={onDone} canSave={amount.state === "value" && amountOk(amount) && date !== null && target !== null}>
      <AmountInput label="Amount refunded" value={amount} onChange={setAmount} emptyMeans="unknown" />
      <DateInput label="Date refunded" value={date} onChange={setDate} />
      <Chips
        legend="What did it refund?"
        value={target}
        options={[...expenses.map((x) => ({ value: x.id, label: `${CATEGORIES.find((c) => c.value === x.category)?.label} ${x.amount.state === "value" ? fmtUsd(x.amount.value) : ""}${x.date ? ` · ${shortDate(x.date)}` : ""}` })), { value: "prior", label: "A charge from an earlier year" }]}
        onChange={setTarget}
      />
      {target === "prior" && (
        <Chips
          legend="Did you deduct that charge on that year's return?"
          value={deducted}
          options={[
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
            { value: "unsure", label: "Not sure" },
          ]}
          onChange={setDeducted}
          hint="If it was deducted, getting the money back counts as income now."
        />
      )}
    </Editor>
  );
}

